import {DocumentContext, DocumentEntityType} from '@sollapay/enums';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {documentKeys} from '@/features/documents/hooks';
import {trustAccountActivationKeys} from '@/features/trust-accounts/activation/hooks';
import {useApiClient} from '@/hooks';

import {buyersApi} from '../api';
import {invalidateBuyerCaches} from '../utils';

import type {UpdateBuyerPayload} from '@sollapay/types';

export function useUpdateBuyerMutation(trustAccountId: string, buyerId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateBuyerPayload) =>
      buyersApi.updateBuyer(client, trustAccountId, buyerId, payload).then(res => res.data),
    onSuccess: (updatedBuyer, payload) => {
      invalidateBuyerCaches(queryClient, trustAccountId, []);
      // The party is firm-wide, so its detail is stale on every account; a re-link returns the new id.
      queryClient.invalidateQueries({
        predicate: ({queryKey}) =>
          queryKey[2] === 'buyers' && queryKey[3] === 'detail' && queryKey[4] === updatedBuyer.id
      });
      if (updatedBuyer.id !== buyerId) {
        // The backend re-points the identity scans to the new party.
        queryClient.invalidateQueries({
          queryKey: documentKeys.list(
            DocumentEntityType.TRUST_ACCOUNT,
            trustAccountId,
            DocumentContext.KYC
          )
        });
      }
      if (payload.approveIdentityChange) {
        // An identity edit signs a new FATCA/CRS supplementary declaration.
        queryClient.invalidateQueries({
          queryKey: trustAccountActivationKeys.compliancePackage(trustAccountId)
        });
      }
    }
  });
}
